import { useState, useRef } from "react";

/**
 * Hook for managing calculator state and operations
 */
export const useMobileCalculator = () => {
  const [calculatorExpression, setCalculatorExpression] = useState<string>("0");
  const hasEvaluatedRef = useRef<boolean>(false);

  // Parse expression string into calculator state
  const parseExpressionIntoCalculator = (expression: string) => {
    // Check if current expression contains operators (user is typing an expression)
    const hasOperators = /[+\-*/]/.test(calculatorExpression);
    const newHasOperators = /[+\-*/]/.test(expression);
    
    // If user is actively typing (has operators) and new expression doesn't, don't overwrite
    // This prevents resetting "50-5" back to "50" when user is typing
    if (hasOperators && !newHasOperators && calculatorExpression !== expression) {
      return; // Don't overwrite user's typing
    }
    
    if (!expression || expression === "") {
      setCalculatorExpression("0");
      return;
    }
    setCalculatorExpression(expression);
  };

  // Get display value for calculator (shows full expression)
  const getCalculatorDisplayValue = (): string => {
    return calculatorExpression;
  };

  // Evaluate expression with proper operator precedence (PEMDAS/BODMAS)
  const evaluateExpression = (expression: string): number => {
    // Remove any trailing operators
    expression = expression.replace(/[+\-*/]$/, "");
    
    // Replace commas with dots for decimal separator
    expression = expression.replace(/,/g, ".");
    
    if (!expression || expression === "") {
      return 0;
    }

    // Tokenize the expression - handle negative numbers properly
    const tokens: (number | string)[] = [];
    let currentNumber = "";
    let i = 0;
    
    // Handle negative at start
    if (expression[0] === "-") {
      currentNumber = "-";
      i = 1;
    }
    
    while (i < expression.length) {
      const char = expression[i];
      
      if ((char >= "0" && char <= "9") || char === ".") {
        currentNumber += char;
      } else if (["+", "-", "*", "/"].includes(char)) {
        // Push the number if we have one
        if (currentNumber !== "" && currentNumber !== "-") {
          tokens.push(parseFloat(currentNumber));
          currentNumber = "";
        }
        
        // Handle consecutive operators (like +- or --)
        if (char === "-" && tokens.length > 0 && typeof tokens[tokens.length - 1] === "string") {
          // Previous token is an operator, treat - as negative sign
          currentNumber = "-";
        } else {
          // Normal operator
          tokens.push(char);
        }
      }
      i++;
    }
    
    // Push the last number if we have one
    if (currentNumber !== "" && currentNumber !== "-") {
      tokens.push(parseFloat(currentNumber));
    }

    if (tokens.length === 0) {
      return 0;
    }

    // First number must be a number, not an operator
    if (tokens.length > 0 && typeof tokens[0] === "string") {
      return 0;
    }

    // Evaluate multiplication and division first (left to right)
    const processedTokens: (number | string)[] = [];
    i = 0;
    
    while (i < tokens.length) {
      if (typeof tokens[i] === "string" && (tokens[i] === "*" || tokens[i] === "/")) {
        // This is a * or / operator
        const prev = processedTokens.pop() as number;
        const operator = tokens[i] as string;
        i++;
        if (i < tokens.length && typeof tokens[i] === "number") {
          const next = tokens[i] as number;
          
          if (operator === "*") {
            processedTokens.push(prev * next);
          } else {
            processedTokens.push(next !== 0 ? prev / next : 0);
          }
          i++;
        } else {
          // Invalid expression
          return 0;
        }
      } else {
        processedTokens.push(tokens[i]);
        i++;
      }
    }

    // Evaluate addition and subtraction (left to right)
    let result = processedTokens[0] as number;
    for (let i = 1; i < processedTokens.length; i += 2) {
      if (i + 1 < processedTokens.length) {
        const operator = processedTokens[i] as string;
        const next = processedTokens[i + 1] as number;
        
        if (operator === "+") {
          result += next;
        } else if (operator === "-") {
          result -= next;
        }
      }
    }

    return result;
  };

  // Handle calculator button clicks - supports chained operations
  const handleCalculatorButton = (btn: string) => {
    let expression = calculatorExpression;

    if (btn === "C") {
      // Clear all
      setCalculatorExpression("0");
      return;
    } else if (btn === "⌫") {
      // Backspace - remove last character
      if (expression.length > 1) {
        expression = expression.slice(0, -1);
        // If empty or just operator, set to "0"
        if (expression === "" || ["+", "-", "*", "/"].includes(expression)) {
          expression = "0";
        }
      } else {
        expression = "0";
      }
    } else if (btn === "." || btn === ",") {
      // Decimal point - add to current number (support both . and ,)
      const lastChar = expression[expression.length - 1];
      const lastNumber = expression.split(/[+\-*/]/).pop() || "";
      
      // Only add decimal if current number doesn't have one
      if (!lastNumber.includes(".") && !lastNumber.includes(",")) {
        if (["+", "-", "*", "/"].includes(lastChar) || expression === "0") {
          expression = expression === "0" ? "0." : expression + "0.";
        } else {
          expression = expression + ".";
        }
      }
    } else if (["+", "-", "*", "/", "x", "÷"].includes(btn)) {
      // Operator - append to expression (support x for * and ÷ for /)
      const lastChar = expression[expression.length - 1];
      const operator = btn === "x" ? "*" : btn === "÷" ? "/" : btn;
      
      // If last character is an operator, replace it
      if (["+", "-", "*", "/"].includes(lastChar)) {
        expression = expression.slice(0, -1) + operator;
      } else {
        // Append operator to expression
        expression = expression + operator;
      }
    } else if (btn === "=") {
      // Equals - evaluate expression (handled by the button onClick, not here)
      return;
    } else if (btn === "00") {
      // Double zero
      if (expression === "0") {
        expression = "0";
      } else {
        expression = expression + "00";
      }
    } else if (btn === "000") {
      // Triple zero
      if (expression === "0") {
        expression = "0";
      } else {
        expression = expression + "000";
      }
    } else {
      // Number
      if (expression === "0") {
        expression = btn;
      } else {
        expression = expression + btn;
      }
    }

    setCalculatorExpression(expression);
  };

  return {
    calculatorExpression,
    setCalculatorExpression,
    hasEvaluatedRef,
    parseExpressionIntoCalculator,
    getCalculatorDisplayValue,
    evaluateExpression,
    handleCalculatorButton,
  };
};
